/**
 * Single-select combobox with typeahead filtering.
 *
 * Behaviour:
 *   - Type → dropdown opens, list filtered by Arabic-normalized substring
 *   - ArrowDown / ArrowUp → cycle highlighted option
 *   - Enter when dropdown open → select highlighted, fire onChange, close
 *   - Enter when dropdown closed (or no items) → fire onSubmit (parent decides)
 *   - Escape → close dropdown without selecting
 *   - Click outside → close
 *   - Mouse hover → highlight
 *   - Mouse click → select
 *
 * Selecting always writes the CANONICAL value from `options`, never the
 * user's raw typing — that's the whole point (eliminates misspellings).
 *
 * Lock states:
 *   - `locked` (correct answer locked in) → disabled, green styling, no dropdown
 *   - `truthIfDone` (game ended, this field was wrong) → disabled, red styling,
 *     displays the truth instead of the user's guess
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { normalizeArabic } from '../lib/arabic'

const MAX_VISIBLE = 8

export function ComboBox({
  label,
  options,
  value,
  onChange,
  onSubmit,
  locked,
  truthIfDone,
  shake,
  autoFocus,
  placeholder,
}: {
  label: string
  options: string[]
  value: string
  /** Called with a CANONICAL option string when the user picks. Empty string clears. */
  onChange: (v: string) => void
  /** Called when the user presses Enter without an active dropdown selection. */
  onSubmit?: () => void
  locked: boolean
  truthIfDone: string | null | undefined
  shake: boolean
  autoFocus?: boolean
  placeholder?: string
}) {
  const [query, setQuery] = useState(value)
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const disabled = locked || !!truthIfDone

  // Keep input in sync if parent clears the value externally (e.g. round advance)
  useEffect(() => { setQuery(value) }, [value])

  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus])

  const filtered = useMemo(() => {
    const q = normalizeArabic(query).trim()
    if (!q) return options.slice(0, MAX_VISIBLE * 4)  // browseable but bounded
    return options
      .filter((o) => normalizeArabic(o).includes(q))
      .slice(0, MAX_VISIBLE * 4)
  }, [options, query])

  // Reset highlight when filter changes
  useEffect(() => { setHighlighted(0) }, [query])

  // Click-outside → close
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Scroll highlighted item into view
  useEffect(() => {
    if (!open || !listRef.current) return
    const el = listRef.current.children[highlighted] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlighted, open])

  function pick(opt: string) {
    setQuery(opt)
    onChange(opt)
    setOpen(false)
    setHighlighted(0)
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) { setOpen(true); return }
      setHighlighted((h) => Math.min(filtered.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) { setOpen(true); return }
      setHighlighted((h) => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      if (open && filtered[highlighted]) {
        e.preventDefault()
        pick(filtered[highlighted])
      } else if (onSubmit) {
        e.preventDefault()
        onSubmit()
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // Display logic: when the game is over and this field was wrong, show
  // the truth in red instead of whatever the user picked.
  const displayValue = truthIfDone && !locked ? truthIfDone : query

  // Outer color treatment
  const inputCls = locked
    ? 'border-leaf-600 bg-leaf-600/15 text-leaf-400'
    : truthIfDone
      ? 'border-rose-600 bg-rose-600/10 text-rose-400'
      : open
        ? 'border-amber-500 bg-ink-900 text-ink-50'
        : 'border-ink-700 bg-ink-700/30 text-ink-50 hover:border-ink-700'

  return (
    <label className="block">
      <span className="text-xs text-ink-500 block mb-1.5 ms-1">{label}</span>
      <div ref={wrapRef} className={`relative ${shake ? 'effeh-shake' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          dir="rtl"
          value={displayValue}
          placeholder={placeholder ?? 'اكتب أو اختر…'}
          onChange={(e) => {
            if (disabled) return
            setQuery(e.target.value)
            onChange('')  // clear the canonical value until they re-pick
            if (!open) setOpen(true)
          }}
          onFocus={() => !disabled && setOpen(true)}
          onClick={() => !disabled && setOpen(true)}
          onKeyDown={onKey}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-expanded={open}
          className={`w-full px-4 py-3 rounded-xl border transition outline-none ${inputCls} ${disabled ? 'cursor-default' : 'cursor-text'}`}
        />

        {/* Dropdown */}
        {open && !disabled && (
          <ul
            ref={listRef}
            role="listbox"
            className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-ink-700 bg-ink-900 shadow-[0_8px_28px_rgba(0,0,0,0.5)]"
          >
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-ink-500 text-center">
                مفيش نتايج في القايمة
              </li>
            )}
            {filtered.map((opt, i) => (
              <li
                key={opt}
                role="option"
                aria-selected={i === highlighted}
                onMouseEnter={() => setHighlighted(i)}
                onMouseDown={(e) => { e.preventDefault(); pick(opt) }}
                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                  i === highlighted
                    ? 'bg-amber-500/15 text-amber-300'
                    : 'text-ink-100 hover:bg-ink-700/40'
                }`}
              >
                {opt}
              </li>
            ))}
          </ul>
        )}
      </div>
      {locked && <p className="text-xs text-leaf-400 mt-1 ms-1">✓ صح</p>}
      {truthIfDone && !locked && <p className="text-xs text-rose-400 mt-1 ms-1">✗ كانت دي الإجابة</p>}
    </label>
  )
}
